import React, { useState, useEffect, useRef } from 'react';
import productEnquiryService from '../../api/productEnquiryService';

const ProductEnquiryForm = ({ productId, productName, onClose }) => {
  // Form validation patterns
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const phonePattern = /^[0-9]{10}$/;
  const closeTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    location: '',
    quantity: '',
    message: '',
    productId: productId,
    productName: productName
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const validateForm = () => {
    const errors = {};
    
    // Validate name
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    // Validate email
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!emailPattern.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Validate mobile
    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!phonePattern.test(formData.mobile)) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    
    // Validate location
    if (!formData.location.trim()) {
      errors.location = 'Location is required';
    }
    
    // Validate quantity
    if (!formData.quantity.trim()) {
      errors.quantity = 'Quantity is required';
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    setValidationErrors(errors);
    
    // If there are validation errors, don't submit
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await productEnquiryService.submitEnquiry(formData);
      setSuccess(true);
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        mobile: '',
        location: '',
        quantity: '',
        message: '',
        productId: productId,
        productName: productName
      });
      // Close the form after 3 seconds to allow user to see success message
      closeTimerRef.current = setTimeout(() => {
        if (onClose) onClose();
      }, 3000);
    } catch (err) {
      let errorMessage = 'Failed to submit enquiry. Please try again later.';
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-md flex items-center justify-center z-50 p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label={`Enquiry form for ${productName}`}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div className="bg-white rounded-[24px] w-full max-w-md p-[32px] md:p-[32px] relative max-h-[90vh] overflow-y-auto my-4 shadow-xl border-t border-gray-100">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close enquiry form"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <h2 className="text-2xl font-bold mb-6 font-['Inter']">{productName}</h2>
        
        {success ? (
          <div className="text-green-600 text-center py-8">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-lg">Thank you for your enquiry!</p>
            <p>We'll get back to you soon.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 font-['Inter']">Your Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`mt-1 w-full border ${validationErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-[80px] py-[16px] px-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-500 text-[14px] font-[400] leading-[100%] font-['Inter']`}
                  placeholder="Name"
                />
                {validationErrors.name && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>
                )}
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">Your Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`mt-1 w-full border ${validationErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-[80px] py-[16px] px-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-500 text-[14px] font-[400] leading-[100%] font-['Inter']`}
                  placeholder="Email"
                />
                {validationErrors.email && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
                )}
              </div>
            </div>
            
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">Your Mobile Number</label>
              <input
                type="tel"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className={`mt-1 w-full border ${validationErrors.mobile ? 'border-red-500' : 'border-gray-300'} rounded-[80px] py-[16px] px-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-500 text-[14px] font-[400] leading-[100%] font-['Inter']`}
                placeholder="Mobile Number"
              />
              {validationErrors.mobile && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.mobile}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location / City</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className={`mt-1 w-full border ${validationErrors.location ? 'border-red-500' : 'border-gray-300'} rounded-[80px] py-[16px] px-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-500 text-[14px] font-[400] leading-[100%] font-['Inter']`}
                placeholder="Address"
              />
              {validationErrors.location && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.location}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">Product Quantity</label>
              <input
                type="text"
                id="quantity"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                className={`mt-1 w-full border ${validationErrors.quantity ? 'border-red-500' : 'border-gray-300'} rounded-[80px] py-[16px] px-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-500 text-[14px] font-[400] leading-[100%] font-['Inter']`}
                placeholder="Quantity Required"
              />
              {validationErrors.quantity && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.quantity}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700">Your Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="mt-1 w-full border border-gray-300 rounded-[18px] py-[16px] px-[16px] focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-500 text-[14px] font-[400] leading-[100%] font-['Inter']"
                placeholder="Your Message"
              ></textarea>
            </div>
            
            {error && (
              <div className="text-red-600 text-sm">
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-[8px] px-[16px] rounded-[18.95px] hover:bg-gray-800 transition-colors font-normal text-[16px] leading-[100%] tracking-[-0.05em] font-['Inter']"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  Send
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </span>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProductEnquiryForm;
